import { useEffect, useState } from 'react';
import { api, ApiError } from '../api/client';
import { useAuth } from '../state/auth';
import { Bandeau, Carte, Champ, Kpi, Modale, Squelette, Vide } from '../components/ui';
import { centimes, dateFr, deviseReference, heureFr, libelleMouvement, montant } from '../lib/format';
import { T } from '../i18n';

interface Tiroir { devise: string; soldeMinor: number; contrevaleurMinor: number }
interface Tiroirs { tiroirs: Tiroir[]; totalReferenceMinor: number; deviseReference: string }
interface Mouvement {
  _id: string; date: string; type: string; libelle: string;
  devise: string; montantMinor: number; contrevaleurMinor: number; mode: string;
  creePar?: { nom: string };
}

const LIBELLE_MODE = (): Record<string, string> => T.caisseExtra.modes;

/**
 * La caisse extra.
 *
 * Une caisse libre : l'agence y note ce qu'elle veut, avec ses propres mots.
 * Elle existe parce que toute agence a de l'argent qui n'entre dans aucune case
 * prévue — un dépôt d'associé, une recette annexe, une avance à régulariser.
 * Sans elle, ces sommes se mêlaient aux encaissements de location et le solde
 * du comptoir ne voulait plus rien dire.
 *
 * Elle se vide quand on veut : vers la caisse, vers la banque, ou dehors. Les
 * deux premiers gardent l'argent dans l'agence — deux lignes liées. Le
 * troisième le fait sortir, et exige alors de dire où il va.
 */
export default function CaisseExtra() {
  const { peut } = useAuth();
  const [t, setT] = useState<Tiroirs | null>(null);
  const [mvts, setMvts] = useState<Mouvement[] | null>(null);
  const [msg, setMsg] = useState('');
  const [erreur, setErreur] = useState('');

  const [saisie, setSaisie] = useState(false);
  const [f, setF] = useState({ libelle: '', montant: '', devise: deviseReference(), sens: 'entree', mode: 'especes' });
  const [vider, setVider] = useState(false);
  const [v, setV] = useState({ montant: '', devise: deviseReference(), vers: 'caisse', libelle: '' });
  const [envoi, setEnvoi] = useState(false);

  const charger = () => Promise.all([
    api.get<Tiroirs>('/caisse/extra/tiroirs'),
    api.get<Mouvement[]>('/caisse/extra/mouvements'),
  ]).then(([a, b]) => { setT(a); setMvts(b); })
    .catch((e) => setErreur(e instanceof ApiError ? e.erreur : T.caisseExtra.erreurChargement));
  useEffect(() => { void charger(); }, []);

  async function ajouter() {
    setEnvoi(true); setErreur('');
    try {
      await api.post('/caisse/extra', {
        devise: f.devise, montantMinor: centimes(Number(f.montant)),
        libelle: f.libelle.trim(), sens: f.sens, mode: f.mode,
      });
      setSaisie(false); setF({ libelle: '', montant: '', devise: deviseReference(), sens: 'entree', mode: 'especes' });
      setMsg(T.caisseExtra.ligneEnregistree);
      await charger();
    } catch (e) { setErreur(e instanceof ApiError ? e.erreur : T.caisseExtra.enregistrementImpossible); }
    finally { setEnvoi(false); }
  }

  async function viderVers() {
    setEnvoi(true); setErreur('');
    try {
      await api.post('/caisse/extra/vider', {
        devise: v.devise, montantMinor: centimes(Number(v.montant)),
        vers: v.vers, libelle: v.libelle.trim() || undefined,
      });
      setVider(false); setV({ montant: '', devise: deviseReference(), vers: 'caisse', libelle: '' });
      setMsg(T.caisseExtra.videe);
      await charger();
    } catch (e) { setErreur(e instanceof ApiError ? e.erreur : T.caisseExtra.transfertImpossible); }
    finally { setEnvoi(false); }
  }

  const tx = T.caisseExtra;
  if (erreur && !t) return <Bandeau type="ko" titre={tx.erreurTitre}>{erreur}</Bandeau>;
  if (!t || !mvts) return <Squelette lignes={5} />;

  const dehors = v.vers === 'dehors';
  const solde = t.tiroirs.find((x) => x.devise === v.devise)?.soldeMinor ?? 0;

  return (
    <>
      <div className="entete">
        <div className="titres">
          <h1>{tx.titre}</h1>
          <p>{tx.sousTitre}</p>
        </div>
        <div className="actions">
          {peut('creer.mouvementCaisse') && (
            <button className="btn btn-p" onClick={() => { setSaisie(true); setErreur(''); }}>{tx.ajouterLigne}</button>
          )}
          {peut('modifier.finance') && t.totalReferenceMinor > 0 && (
            <button className="btn" onClick={() => { setVider(true); setErreur(''); }}>{tx.vider}</button>
          )}
        </div>
      </div>

      {msg && <div style={{ marginBottom: 14 }}><Bandeau type="ok">{msg}</Bandeau></div>}
      {erreur && <div style={{ marginBottom: 14 }}><Bandeau type="ko">{erreur}</Bandeau></div>}

      <div className="grille g3" style={{ marginBottom: 16 }}>
        <Kpi libelle={tx.kpi.dedans} valeur={montant(t.totalReferenceMinor)} className="signature-kpi" />
        <Kpi libelle={tx.kpi.devises} valeur={t.tiroirs.filter((x) => x.soldeMinor !== 0).length || 1}
             detail={t.tiroirs.filter((x) => x.soldeMinor !== 0).map((x) => `${x.devise} ${montant(x.soldeMinor)}`).join(' · ') || tx.kpi.vide} />
        <Kpi libelle={tx.kpi.lignes} valeur={mvts.length} detail={tx.kpi.lignesDetail} />
      </div>

      <Carte titre={tx.historique} pad={false}>
        {mvts.length === 0 ? (
          <div className="carte-p"><Vide titre={tx.videTitre}>
            {tx.videTexte}
          </Vide></div>
        ) : (
          <div className="enveloppe-tbl">
            <table className="tbl">
              <thead>
                <tr><th>{tx.colonnes.date}</th><th>{tx.colonnes.libelle}</th><th>{tx.colonnes.nature}</th><th>{tx.colonnes.payeEn}</th><th>{tx.colonnes.par}</th><th className="droite">{tx.colonnes.montant}</th></tr>
              </thead>
              <tbody>
                {mvts.map((m) => {
                  const sortant = m.type === 'retrait' || m.type === 'transfert_sortie' || m.type === 'depense';
                  return (
                    <tr key={m._id}>
                      <td className="mini sourdine">{dateFr(m.date)}<div>{heureFr(m.date)}</div></td>
                      <td>{m.libelle}</td>
                      <td className="mini sourdine">{libelleMouvement(m.type)}</td>
                      <td className="mini sourdine">{LIBELLE_MODE()[m.mode] ?? m.mode}</td>
                      <td className="mini sourdine">{m.creePar?.nom ?? '—'}</td>
                      <td className="droite montant" style={{ color: sortant ? 'var(--danger)' : 'var(--succes)' }}>
                        {sortant ? '−' : '+'} {montant(m.contrevaleurMinor)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Carte>

      {saisie && (
        <Modale titre={tx.ajouterLigne} onFermer={() => setSaisie(false)}>
          <div className="actions" style={{ marginBottom: 12 }}>
            <button className={`btn btn-s ${f.sens === 'entree' ? 'btn-p' : ''}`}
                    onClick={() => setF({ ...f, sens: 'entree' })}>{tx.saisie.entre}</button>
            <button className={`btn btn-s ${f.sens === 'sortie' ? 'btn-d' : ''}`}
                    onClick={() => setF({ ...f, sens: 'sortie' })}>{tx.saisie.sort}</button>
          </div>
          <Champ label={tx.saisie.libelle} aide={tx.saisie.libelleAide}>
            <input value={f.libelle} autoFocus onChange={(e) => setF({ ...f, libelle: e.target.value })}
                   placeholder={tx.saisie.libelleExemple} />
          </Champ>
          <div className="grille g3" style={{ marginTop: 12 }}>
            <Champ label={tx.saisie.montant}>
              <input type="number" min={0} step={100} value={f.montant}
                     onChange={(e) => setF({ ...f, montant: e.target.value })} />
            </Champ>
            <Champ label={tx.saisie.devise}>
              <select value={f.devise} onChange={(e) => setF({ ...f, devise: e.target.value })}>
                {t.tiroirs.map((x) => <option key={x.devise} value={x.devise}>{x.devise}</option>)}
              </select>
            </Champ>
            <Champ label={tx.saisie.payeEn}>
              <select value={f.mode} onChange={(e) => setF({ ...f, mode: e.target.value })}>
                {Object.entries(LIBELLE_MODE()).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
              </select>
            </Champ>
          </div>
          <div className="actions" style={{ marginTop: 16, justifyContent: 'flex-end' }}>
            <button className="btn" onClick={() => setSaisie(false)}>{T.commun.actions.annuler}</button>
            <button className="btn btn-p" disabled={envoi || f.libelle.trim().length < 2 || !(Number(f.montant) > 0)}
                    onClick={() => void ajouter()}>{T.commun.actions.enregistrer}</button>
          </div>
        </Modale>
      )}

      {vider && (
        <Modale titre={tx.vider} onFermer={() => setVider(false)}>
          <p className="mini sourdine" style={{ marginTop: 0 }}>
            {tx.vidage.ilYA}<b>{montant(solde)}</b>{tx.vidage.enDevise(v.devise)}
          </p>
          <div className="actions" style={{ marginBottom: 12 }}>
            <button className={`btn btn-s ${v.vers === 'caisse' ? 'btn-p' : ''}`}
                    onClick={() => setV({ ...v, vers: 'caisse' })}>{tx.vidage.versCaisse}</button>
            <button className={`btn btn-s ${v.vers === 'banque' ? 'btn-p' : ''}`}
                    onClick={() => setV({ ...v, vers: 'banque' })}>{tx.vidage.versBanque}</button>
            <button className={`btn btn-s ${v.vers === 'dehors' ? 'btn-d' : ''}`}
                    onClick={() => setV({ ...v, vers: 'dehors' })}>{tx.vidage.ailleurs}</button>
          </div>
          <div className="grille g2">
            <Champ label={tx.saisie.montant}>
              <input type="number" min={0} step={100} value={v.montant} autoFocus
                     onChange={(e) => setV({ ...v, montant: e.target.value })} />
            </Champ>
            <Champ label={tx.saisie.devise}>
              <select value={v.devise} onChange={(e) => setV({ ...v, devise: e.target.value })}>
                {t.tiroirs.map((x) => <option key={x.devise} value={x.devise}>{x.devise}</option>)}
              </select>
            </Champ>
          </div>
          <div style={{ marginTop: 12 }}>
            <Champ label={dehors ? tx.vidage.ouVa : tx.vidage.libelle}
                   aide={dehors ? tx.vidage.ouVaAide : tx.vidage.facultatif}
                   erreur={dehors && !v.libelle.trim() ? tx.vidage.ouVaErreur : undefined}>
              <input value={v.libelle} onChange={(e) => setV({ ...v, libelle: e.target.value })}
                     placeholder={dehors ? tx.vidage.exempleDehors : tx.vidage.exempleLibelle} />
            </Champ>
          </div>
          <div style={{ marginTop: 12 }}>
            <Bandeau type={dehors ? 'ko' : 'info'}
                     titre={dehors ? tx.vidage.titreDehors : tx.vidage.titrePoche}>
              {dehors
                ? tx.vidage.texteDehors
                : tx.vidage.textePoche(v.vers === 'banque')}
            </Bandeau>
          </div>
          <div className="actions" style={{ marginTop: 16, justifyContent: 'flex-end' }}>
            <button className="btn" onClick={() => setVider(false)}>{T.commun.actions.annuler}</button>
            <button className="btn btn-p"
                    disabled={envoi || !(Number(v.montant) > 0) || (dehors && !v.libelle.trim())}
                    onClick={() => void viderVers()}>{tx.vidage.bouton}</button>
          </div>
        </Modale>
      )}
    </>
  );
}
